import React from 'react';
import { ICustomHeader, gridControlType } from 'app/models/Grid';
import NoteIcon from 'app/assets/images/Notes.png';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { CredentialingLookup } from 'app/models/Credentialing/CredentialingLookups';
import {
  getDate,
  getDropDownValue,
  getLookups,
  getDateNoFormat,
  getDropDownDescription,
  getCredentialName,
  getDateText,
  getCredentialNameNoFormat,
  getStateValue,
  getStateFilterOptions,
  getStateDescription,
  detailColumnsAndRows,
  getMetadataForReqID,
  getCredentialNameBold,
} from './tableHelpers';
import { ICredentials } from 'app/models/Credentialing/Credentials';
import HistoryIcon from '@mui/icons-material/History';
import { groupBy, nestedChildren } from 'app/helpers/arrayHelpers';
import VersionDetailsHoverView from './VersionDetailsHoverView';
import { AttachmentAction, CredentialAttachmentToolTip } from '../Attachments/AttachmentHoverView';
import { childSorting } from './HumanResources/HumanResourcesGroupingHelper';

const getMainTableColumns = (
  viewingDuplicates: boolean = false,
  historical: boolean = false,
  sortable: boolean = true,
) => {
  const headers: ICustomHeader[] = [
    {
      title: 'credentialName',
      value: 'Credential Name',
      sortable: sortable,
      sortByField: 'credentialNameNoFormat',
    },
    {
      title: 'typeID',
      value: 'Type',
      sortByField: 'typeIDDescription',
      sortable: sortable,
      control: {
        type: gridControlType.dropDown,
      },
    },
    {
      title: 'stateID',
      value: 'State',
      sortByField: 'stateIDDescription',
      sortable: sortable,
      control: {
        type: gridControlType.dropDown,
      },
    },
    {
      title: 'dateCompleted',
      value: 'Date Completed',
      sortable: sortable,
      sortByField: 'dateCompletedNoFormat',
      control: {
        type: gridControlType.date,
      },
    },
    {
      title: 'expirationDate',
      value: 'Expiration Date',
      sortable: sortable,
      defaultSort: !viewingDuplicates,
      sortByField: 'expirationDateNoFormat',
      control: {
        type: gridControlType.date,
      },
    },
    {
      title: 'lastUpdate',
      value: 'Updated Date',
      sortable: sortable,
      defaultSort: viewingDuplicates,
      sortByField: 'lastUpdateNoFormat',
      control: {
        type: gridControlType.label,
      },
    },
    {
      title: 'versioning',
      value: 'Versions',
      icon: true,
      versioning: !historical,
    },
    {
      title: 'documentIcon',
      value: 'Document',
      // clickable: true,
      navigationIds: [''],
      navigationExp: '/',
      hasHoverView: true,
      customHoverView: true,
    },
  ];
  return headers;
};

export const groupedEducations = list => {
  const data1 = groupBy<keyof ICredentials>(
    list.filter(item => item.stateID !== null && item.stateID !== undefined),
    ['credentialName', 'stateID'],
    [
      { item: 'expirationDate', type: 'shortDate' },
      { item: 'dateCompleted', type: 'shortDate' },
      { item: 'issuedDate', type: 'shortDate' },
      { item: 'lastUpdate', type: 'longDate' },
    ],
  );

  const data2 = groupBy<keyof ICredentials>(
    list.filter(item => item.stateID === null || item.stateID === undefined),
    ['credentialName', 'typeID'],
    [
      { item: 'expirationDate', type: 'shortDate' },
      { item: 'dateCompleted', type: 'shortDate' },
      { item: 'issuedDate', type: 'shortDate' },
      { item: 'lastUpdate', type: 'longDate' },
    ],
  );
  const finalData = { ...data1, ...data2 };
  return finalData;
};

const flattenData = (
  educations: ICredentials[],
  lookups: CredentialingLookup[],
  viewingDuplicates = false,
  onAttachmentAction: (
    action: AttachmentAction,
    reqID: number,
    requirementTypeID: number,
    travelerId: number,
    brandId: number,
  ) => void,
  level: number = 0,
  expandable: boolean = true,
) => {
  let nestedItems = educations;
  if (level === 0) {
    const grouped = groupedEducations(educations);
    nestedItems = nestedChildren(grouped);
  }
  if (level > 0) {
    nestedItems = childSorting(nestedItems, lookups);
  }
  const credentials: any[] = [];
  nestedItems?.forEach((item: ICredentials) => {
    const fieldLookups = getMetadataForReqID(lookups, item);
    const l: any = {
      rowID: item.requirementID,
      credentialName:
        level === 0 ? (
          getCredentialName(item.requirementType, item.credentialName)
        ) : (
          <VersionDetailsHoverView
            credential={[
              ...detailColumnsAndRows(
                item,
                fieldLookups,
                ['StateID', 'DateCompleted', 'ExpirationDate', 'LastUpdate', 'TypeID'],
                true,
              ),
            ]}
            fieldLookups={fieldLookups}
            title={getCredentialName(item.requirementType, item.credentialName)}
            titleBold={getCredentialNameBold(item.requirementType, item.credentialName)}
          />
        ),
      credentialNameNoFormat: getCredentialNameNoFormat(item.requirementType, item.credentialName),
      stateID: getStateValue(fieldLookups, 'StateID', item.stateID),
      stateIDFilterOptions: getStateFilterOptions(fieldLookups, 'StateID'),
      stateIDDescription: getStateDescription(fieldLookups, 'StateID', item.stateID),
      dateCompleted: getDate(fieldLookups, 'DateCompleted', item.dateCompleted),
      dateCompletedNoFormat: getDateNoFormat(fieldLookups, 'DateCompleted', item.dateCompleted),
      expirationDate: getDate(fieldLookups, 'ExpirationDate', item.expirationDate),
      expirationDateNoFormat: getDateNoFormat(fieldLookups, 'ExpirationDate', item.expirationDate),
      lastUpdate: getDateText(fieldLookups, 'LastUpdate', item.lastUpdate),
      lastUpdateNoFormat: getDateNoFormat(fieldLookups, 'LastUpdate', item.lastUpdate),
      typeID: getDropDownValue(fieldLookups, 'TypeID', item.typeID),
      typeIDFilterOptions: getLookups(fieldLookups, 'TypeID', item.typeID),
      typeIDDescription: getDropDownDescription(fieldLookups, 'TypeID', item.typeID),
      versioning: level === 0 ? <HistoryIcon /> : null,
      documentIcon: NoteIcon,
      moreIcon: <MoreVertIcon />,
      expandable: expandable,
      detailRows: [
        [
          ...detailColumnsAndRows(item, fieldLookups, [
            'StateID',
            'DateCompleted',
            'ExpirationDate',
            'LastUpdate',
            'TypeID',
          ]),
        ],
      ],
      documentIconHoverViewComponent: (
        <CredentialAttachmentToolTip
          onSelect={onAttachmentAction}
          requirementID={item.requirementID}
          requirementTypeID={item.requirementTypeID}
          travelerId={0}
          brandId={0}
        />
      ),
      versionRows:
        level === 0 && item['children'] !== undefined
          ? flattenData(item['children'], lookups, false, onAttachmentAction, level + 1, false)
          : null,
      versionColumns: getMainTableColumns(true, false, true),
    };
    credentials.push(l);
  });
  if (level === 0) {
    return credentials.sort(
      viewingDuplicates
        ? (a: any, b: any) => (a.lastUpdateNoFormat < b.lastUpdateNoFormat ? 1 : -1)
        : (a: any, b: any) => (a.expirationDateNoFormat > b.expirationDateNoFormat ? 1 : -1),
    );
  } else return credentials;
};

export const educationsData = {
  getTableColumns: getMainTableColumns,
  getTableRows: flattenData,
};
